/**
 * Shared styled-components for authentication pages.
 *
 * All colors, fonts, spacing, and border radii reference the theme
 * provided by AuthLayout's ThemeProvider (dark theme).
 *
 * Usage: import { Title, Subtitle, ... } from '../auth.styled';
 */
import styled, { css } from 'styled-components';

// =============================================================================
// Typography
// =============================================================================

export const Title = styled.h1`
  ${({ theme }) => css`
    font-size: ${theme.font.headlineMedium.fontSize};
    line-height: ${theme.font.headlineMedium.lineHeight};
    font-weight: 700;
    color: ${theme.colors.onSurface};
    font-family: ${theme.font.headlineMedium.fontFamily}, sans-serif;
    margin: 0 0 ${theme.spacing.level3};
  `}
`;

export const Subtitle = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.font.bodyLarge.fontSize};
    line-height: ${theme.font.bodyLarge.lineHeight};
    color: ${theme.colors.onSurfaceVariant};
    font-family: ${theme.font.bodyLarge.fontFamily}, sans-serif;
    margin: 0 0 ${theme.spacing.level7};
  `}
`;

// =============================================================================
// Form elements
// =============================================================================

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.level5};
`;

export const Input = styled.input<{ $error?: boolean }>`
  ${({ theme, $error }) => css`
    width: 100%;
    padding: ${theme.spacing.level4} ${theme.spacing.level5};
    background: ${theme.colors.surfaceContainerLow};
    border: 1px solid ${$error ? theme.colors.error : theme.colors.outlineVariant};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.onSurface};
    font-size: ${theme.font.bodyLarge.fontSize};
    font-family: ${theme.font.bodyLarge.fontFamily}, sans-serif;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: ${theme.colors.primary};
    }

    &::placeholder {
      color: ${theme.colors.outline};
    }
  `}
`;

export const SubmitButton = styled.button<{ $loading?: boolean }>`
  ${({ theme, $loading }) => css`
    width: 100%;
    padding: 14px;
    background: ${theme.colors.primary};
    color: ${theme.colors.onPrimary};
    border: none;
    border-radius: ${theme.borderRadius.small};
    font-size: ${theme.font.titleMedium.fontSize};
    font-weight: ${theme.font.titleMedium.fontWeight};
    font-family: ${theme.font.titleMedium.fontFamily}, sans-serif;
    cursor: pointer;
    opacity: ${$loading ? 0.7 : 1};
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: ${theme.colors.primaryContainer};
    }

    &:disabled {
      cursor: not-allowed;
    }
  `}
`;

// =============================================================================
// Feedback / status
// =============================================================================

export const ErrorText = styled.p`
  ${({ theme }) => css`
    color: ${theme.colors.error};
    font-size: ${theme.font.bodySmall.fontSize};
    margin: -${theme.spacing.level3} 0 0;
  `}
`;

export const ErrorBox = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.level4} ${theme.spacing.level5};
    background: ${theme.colors.error}1A;
    border: 1px solid ${theme.colors.error}4D;
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.error};
    font-size: ${theme.font.bodyMedium.fontSize};
  `}
`;

export const SuccessBox = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.level4} ${theme.spacing.level5};
    background: ${theme.colors.success}1A;
    border: 1px solid ${theme.colors.success}4D;
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.success};
    font-size: ${theme.font.bodyMedium.fontSize};
  `}
`;

export const StatusMessage = styled.p<{ $error?: boolean }>`
  ${({ theme, $error }) => css`
    font-size: ${theme.font.bodyMedium.fontSize};
    color: ${$error ? theme.colors.error : theme.colors.success};
    margin: ${theme.spacing.level5} 0;
  `}
`;

// =============================================================================
// Navigation / links
// =============================================================================

export const Divider = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.level5};
    color: ${theme.colors.outline};
    font-size: ${theme.font.bodySmall.fontSize};

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${theme.colors.outlineVariant};
    }
  `}
`;

export const LinkText = styled.p`
  ${({ theme }) => css`
    text-align: center;
    color: ${theme.colors.onSurfaceVariant};
    font-size: ${theme.font.bodyMedium.fontSize};

    a {
      color: ${theme.colors.primary};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  `}
`;

export const ForgotLink = styled.div`
  ${({ theme }) => css`
    text-align: left;
    margin-top: auto;
    padding-top: ${theme.spacing.level7};

    a {
      color: ${theme.colors.primary};
      font-size: ${theme.font.bodyMedium.fontSize};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  `}
`;

export const Footer = styled.p`
  ${({ theme }) => css`
    text-align: center;
    color: ${theme.colors.outline};
    font-size: ${theme.font.bodySmall.fontSize};
    margin-top: auto;
    padding-top: ${theme.spacing.level6};

    a {
      color: ${theme.colors.primary};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  `}
`;

// =============================================================================
// Checkbox
// =============================================================================

export const CheckboxRow = styled.label`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacing.level3};
    cursor: pointer;
    font-size: ${theme.font.bodyMedium.fontSize};
    color: ${theme.colors.onSurfaceVariant};
    line-height: 1.4;

    input[type='checkbox'] {
      appearance: none;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin-top: 2px;
      border: 1.5px solid ${theme.colors.outline};
      border-radius: 4px;
      background: ${theme.colors.surfaceContainerLow};
      cursor: pointer;
      transition: all 0.15s;

      &:checked {
        background: ${theme.colors.primary};
        border-color: ${theme.colors.primary};
      }

      &:checked::after {
        content: '\\2713';
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: ${theme.colors.onPrimary};
        height: 100%;
      }

      &:focus-visible {
        outline: 2px solid ${theme.colors.primary};
        outline-offset: 2px;
      }
    }

    a {
      color: ${theme.colors.primary};
      text-decoration: none;
      font-weight: ${theme.font.labelLarge.fontWeight};

      &:hover,
      &:focus {
        opacity: 0.75;
      }
    }
  `}
`;

// =============================================================================
// Utility
// =============================================================================

export const HighlightEmail = styled.span`
  ${({ theme }) => css`
    color: ${theme.colors.primary};
    font-weight: 500;
  `}
`;

export const Message = styled.p`
  ${({ theme }) => css`
    font-size: ${theme.font.bodyLarge.fontSize};
    line-height: 1.6;
    color: ${theme.colors.onSurfaceVariant};
    margin: 0 0 ${theme.spacing.level5};
  `}
`;

/**
 * Inline link that inherits the parent's colour (typically used inside
 * ErrorBox to point the user to Aibuild CAM).
 */
export const InheritLink = styled.a`
  color: inherit;
  font-weight: 600;
`;

/**
 * Button styled as a link, used where an anchor with href="#" would
 * otherwise be used (e.g. opening EULA in a new tab via JS).
 */
export const LinkButton = styled.button`
  ${({ theme }) => css`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${theme.colors.primary};
    font: inherit;
    font-weight: ${theme.font.labelLarge.fontWeight};
    text-decoration: none;

    &:hover,
    &:focus {
      opacity: 0.75;
    }
  `}
`;
